import React, { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { selectNodeById, setPreviousSibling, setNextSibling, setParent } from '../../controllers/treeEngine'
import { startTextEdit, moveCursorTo, endTextEdit } from '../../store/cursor'
import nodeTypesToClasses from './nodeTypesToClasses'
import { NodeTextInput } from './NodeTextInput'
import { Markdown } from '../Markdown'
import { scrollIfTitleNodeChanged } from '../../controllers/autoScroll'
import { Label } from './Label'
import { ChildrenIcons } from './ChildrenIcons'
import { ListItemChildrenButton } from './ListItemChildrenButton'
import { ROOT_NODE_ID } from '../../store/migrations/data-structure-conversion'

const getNodeClasses = (type, isCursorNode, hasDragged) => {
    const nodeClasses = ['list-item']
    nodeClasses.push(nodeTypesToClasses[type])
    if (isCursorNode) nodeClasses.push('cursor-node')
    if (hasDragged) nodeClasses.push('node-dragged')

    return nodeClasses.join(' ')
}

const NodeComponent = (props) => {
    const { editingText, isCursorNode, text, id, type, previousSiblingId, nextSiblingId, parentId } = props
    const { moveCursorTo, startTextEdit, endTextEdit } = props
    const refTextArea = useRef()

    useEffect(() => {
        isCursorNode && scrollIfTitleNodeChanged()
    }, [isCursorNode])

    const [dragStartX, setDragStartX] = useState(0)
    const [dragStartY, setDragStartY] = useState(0)
    const [dragLastX, dragSetLastX] = useState(0)
    const [dragLastY, dragSetLastY] = useState(0)
    const [dragging, setDragging] = useState(false)
    const [hasDragged, _setHasDragged] = useState(false)
    const [mousePressedNotDragged, setMousePressedNotDragged] = useState(false)
    const [style, setStyle] = useState()

    const setHasDragged = (hasDragged) => {
        _setHasDragged(hasDragged)
        if (hasDragged) {
            setMousePressedNotDragged(false)
        }
    }

    const move = (x, y) => {
        dragSetLastX(x)
        dragSetLastY(y)
        const dx = x - dragStartX
        const dy = y - dragStartY
        setStyle({ transform: `translate(${dx}px, ${dy}px)` })
        setHasDragged(true)
    }

    const mouseMove = (event) => {
        if (isCursorNode && dragging) {
            event.preventDefault()
            move(event.pageX, event.pageY)
        }
    }

    const touchMove = (event) => {
        if (isCursorNode && dragging) {
            event.preventDefault()
            const touch = event.touches[0]
            move(touch.pageX, touch.pageY)
        }
    }

    useLayoutEffect(() => {
        if (isCursorNode) {
            window.addEventListener('mousemove', mouseMove)
            window.addEventListener('touchmove', touchMove, { passive: false })
            return () => {
                window.removeEventListener('mousemove', mouseMove)
                window.removeEventListener('touchmove', touchMove)
            }
        }
    }, [isCursorNode, dragging, dragStartX, dragStartY])

    const mouseDown = (event) => {
        event.preventDefault() // allow editing text from a node while other is being dragged
        setDragStartX(event.pageX)
        setDragStartY(event.pageY)
        setDragging(true)
    }

    const onTouchStart = (event) => {
        if (event.touches.length === 1) {
            const touch = event.touches[0]
            setDragStartX(touch.pageX)
            setDragStartY(touch.pageY)
            setDragging(true)
        }
    }

    const end = (x, y) => {
        setDragging(false)
        setHasDragged(false)
        setStyle()

        if (hasDragged) {
            const dy = y - dragStartY
            const dx = x - dragStartX

            if (Math.abs(dy) > Math.abs(dx)) {
                if (dy < -30) {
                    if (previousSiblingId) setNextSibling(id, previousSiblingId)
                }
                if (dy > 30) {
                    if (nextSiblingId) setPreviousSibling(id, nextSiblingId)
                }
            } else {
                if (dx < -50) {
                    if (parentId !== ROOT_NODE_ID) setPreviousSibling(id, parentId)
                }
                if (dx > 50) {
                    if (previousSiblingId) setParent(id, previousSiblingId)
                }
            }
        }
    }

    const mouseUp = (event) => {
        end(event.pageX, event.pageY)
    }

    const touchEnd = () => {
        // buggy with multi-touch
        end(dragLastX, dragLastY)
    }

    const onClick = (event) => {
        if (isCursorNode) {
            !event.target.matches('a') && startTextEdit()
        } else {
            endTextEdit()
            moveCursorTo(id)
        }
    }

    const textMouseDown = () => {
        setMousePressedNotDragged(true)
    }

    const textMouseUp = (event) => {
        if (mousePressedNotDragged) {
            onClick(event)
            setMousePressedNotDragged(false)
        }
        mouseUp(event)
    }

    useEffect(() => {
        const element = refTextArea.current
        element.addEventListener('mousedown', textMouseDown)
        element.addEventListener('mouseup', textMouseUp)
        return () => {
            element.removeEventListener('mousedown', textMouseDown)
            element.removeEventListener('mouseup', textMouseUp)
        }
    }, [mousePressedNotDragged])

    return (
        <div
            className={getNodeClasses(type, isCursorNode, hasDragged)}
            onMouseDown={mouseDown}
            onTouchStart={onTouchStart}
            onTouchEnd={touchEnd}
            onMouseUp={mouseUp}
            onMouseLeave={mouseUp}
            style={style}
        >
            <Label nodeId={id} />
            <div className="list-item-content" ref={refTextArea}>
                {editingText && isCursorNode ? (
                    <NodeTextInput id={id} value={text} />
                ) : (
                    // the extra <span> helps removing strike-through from cancelled tasks when they are being edited
                    <span>
                        <Markdown>{text}</Markdown>
                    </span>
                )}
                <ChildrenIcons nodeId={id} />
            </div>

            <ListItemChildrenButton nodeId={id} />
        </div>
    )
}

const mapStateToProps = (state, ownProps) => {
    const { text, id, type, previousSiblingId, nextSiblingId, parentId } = selectNodeById(ownProps.nodeId)

    return {
        editingText: state.cursor.editingText,
        text,
        id,
        type,
        previousSiblingId,
        nextSiblingId,
        parentId
    }
}

const mapDispatchToProps = {
    startTextEdit,
    endTextEdit,
    moveCursorTo
}

export const Node = connect(mapStateToProps, mapDispatchToProps)(NodeComponent)
